import {
  useDocumentTableColumns,
  DocumentsTable,
} from '@/components/dashboard/documents-page/DocumentsTable'
import DocumentsSidebar from '@/components/dashboard/documents-page/DocumentsSidebar'
import { TabsList, TabsTrigger } from '@/components/ui/tabs'
import ErrorDocumentPage from '@/components/dashboard/documents-page/ErrorDocumentPage'
import { ListIcon } from 'lucide-react'
import { getIdTokenUsingQueryClient } from '@/data/queries'
import { getSortedRowModel } from '@tanstack/react-table'
import { getPaginationRowModel } from '@tanstack/react-table'
import { getCoreRowModel } from '@tanstack/react-table'
import {
  createFileRoute,
  redirect,
  useRouteContext,
} from '@tanstack/react-router'
import { useReactTable, type SortingState } from '@tanstack/react-table'
import { useMemo, useState, useCallback } from 'react'
import { Tabs } from '@/components/ui/tabs'
import { LayoutGridIcon } from 'lucide-react'
import { Separator } from '@/components/ui/separator'
import { GridView } from '@/components/dashboard/documents-page/DocumentsPageContent'
import { useLocalStorageState } from '@/hooks/useLocalStorageState'
import { LoadingDocumentsCard } from '@/components/loading-ui/LoadingDocumentsCard'
import { Input } from '@/components/ui/input'
import { debounce } from 'lodash'
import { getFilteredRowModel } from '@tanstack/react-table'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useSelectedTenantStore } from '@/stores/useSelectedTenantStore'
import { useIdTokenAndTenantId } from '@/hooks/useIdTokenAndTenantId'
import {
  createdByMeDocumentsOptions,
  orgPermissionsCurrentUserOptions,
  tenantProjectsOptions,
} from '@/data/queryOptions'
import { REFRESH_INTERVAL } from '@/data/constants'
import { useIsMobile } from '@/hooks/use-mobile'
import { cn } from '@/lib/utils'
import { DashboardLayout } from '@/routes/_authenticated/_dashboard'
import {
  filterDocumentsByAccess,
  getVisibleProjects,
  getWorkspaceAccess,
} from '@/data/entities'

export const Route = createFileRoute(
  '/_authenticated/org/$orgId/documents/created-by-me',
)({
  component: RouteComponent,
  head: () => ({
    meta: [
      {
        title: 'Created by Me - LineWise',
      },
      {
        name: 'description',
        content: 'Created by Me Page',
      },
    ],
  }),
  loader: async ({ context, params }) => {
    const { queryClient, authState } = context
    const TENANT_ID = params.orgId
    // Sync store with URL param
    const store = useSelectedTenantStore.getState()
    if (TENANT_ID && store.selectedTenantId !== TENANT_ID) {
      store.setSelectedTenantId(TENANT_ID)
    }
    if (!TENANT_ID) {
      throw redirect({ to: '/select-tenant' })
    }
    const idToken = await getIdTokenUsingQueryClient({
      queryClient,
      authState: authState!,
    })
    return Promise.all([
      queryClient.ensureQueryData(
        createdByMeDocumentsOptions(idToken, TENANT_ID, authState!.uid),
      ),
      queryClient.ensureQueryData(tenantProjectsOptions(TENANT_ID!, idToken!)),
    ])
  },
  pendingComponent: LoadingDocumentsCard,
  errorComponent: ({ error }) => <ErrorDocumentPage error={error} />,
})

function RouteComponent() {
  const { authState, isOrgAdmin, isOrgEditor } = useRouteContext({
    from: '/_authenticated',
  })
  const { idToken, TENANT_ID } = useIdTokenAndTenantId()
  const isMobile = useIsMobile()

  const { data: documents } = useSuspenseQuery({
    ...createdByMeDocumentsOptions(idToken, TENANT_ID!, authState!.uid),
    refetchInterval: REFRESH_INTERVAL,
    retry: (failureCount, error) => {
      console.error(
        'Error fetching documents, retrying attempt',
        failureCount + 1,
        error,
      )
      return failureCount < 5
    },
  })

  const { data: projects } = useSuspenseQuery(
    tenantProjectsOptions(TENANT_ID!, idToken!),
  )
  const { data: orgPermissions } = useSuspenseQuery(
    orgPermissionsCurrentUserOptions(TENANT_ID!, idToken!),
  )
  const workspaceAccess = useMemo(
    () => getWorkspaceAccess(orgPermissions, TENANT_ID!),
    [orgPermissions, TENANT_ID],
  )
  const visibleProjects = useMemo(
    () =>
      getVisibleProjects(projects ?? [], {
        isOrgAdmin: !!isOrgAdmin,
        isOrgEditor: !!isOrgEditor,
        workspaceAccess,
      }),
    [projects, workspaceAccess, isOrgAdmin, isOrgEditor],
  )
  const visibleDocuments = useMemo(
    () =>
      filterDocumentsByAccess(documents ?? [], workspaceAccess, {
        isOrgAdmin: !!isOrgAdmin,
        isOrgEditor: !!isOrgEditor,
      }),
    [documents, workspaceAccess, isOrgAdmin, isOrgEditor],
  )

  const [viewType, setViewType] = useLocalStorageState<
    'grid' | 'list' | 'table'
  >('documents-view-type', 'grid')
  const [sorting, setSorting] = useState<SortingState>([
    {
      id: 'createdAt',
      desc: true,
    },
  ])
  const [globalFilter, setGlobalFilter] = useState('')

  // Create a memoized debounced setter for the global filter
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSetGlobalFilter = useCallback(
    debounce((value: string) => {
      setGlobalFilter(value)
    }, 300),
    [],
  )

  const columns = useDocumentTableColumns(visibleDocuments)
  const table = useReactTable({
    data: visibleDocuments,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
      globalFilter,
      pagination: {
        pageIndex: 0,
        pageSize: 30, // TODO: set up pagination from server
      },
    },
    columnResizeMode: 'onChange',
    defaultColumn: {
      size: 80,
      minSize: 80,
    },
  })

  return (
    <DashboardLayout>
      <main
        className={cn('main-container', isMobile && 'mt-15 mb-20 flex-col')}
      >
        <DocumentsSidebar projects={visibleProjects} />
        <div className="dashboard-page-container">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl">My Documents</h1>
            <Tabs
              defaultValue="grid"
              onValueChange={(value) =>
                setViewType(value as 'grid' | 'list' | 'table')
              }
            >
              <TabsList>
                <TabsTrigger value="grid">
                  <LayoutGridIcon />
                </TabsTrigger>
                <TabsTrigger value="list">
                  <ListIcon />
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <Input
            placeholder="Search documents"
            className="min-h-9 max-w-md"
            onChange={(e) => debouncedSetGlobalFilter(e.target.value)}
          />
          <Separator />
          {viewType === 'grid' ? (
            <GridView
              documents={table.getRowModel().rows.map((row) => row.original)}
              className="grow"
              projects={projects ?? []}
              currentProjectId={null}
            />
          ) : (
            <DocumentsTable table={table} className="min-h-0 grow" />
          )}
        </div>
      </main>
    </DashboardLayout>
  )
}
